import { ENCRYPTION_KEY } from 'src/config-global';

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function fromBase64(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

async function sha256(input: string): Promise<ArrayBuffer> {
  const enc = new TextEncoder();
  return crypto.subtle.digest('SHA-256', enc.encode(input));
}

async function importAesKeyFromSecret(secret: string): Promise<CryptoKey> {
  const hash = await sha256(secret);
  return crypto.subtle.importKey(
    'raw',
    hash,
    { name: 'AES-GCM' },
    false,
    ['encrypt']
  );
}

async function importAesKeyFromSecretForDecrypt(secret: string): Promise<CryptoKey> {
  const hash = await sha256(secret);
  return crypto.subtle.importKey(
    'raw',
    hash,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );
}

export async function encryptString(plainText: string): Promise<string> {
  const secret = ENCRYPTION_KEY || '';
  const enc = new TextEncoder();

  // Fallback to base64 encoding if no key is configured
  if (!secret) {
    return `b64.${btoa(plainText)}`;
  }

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await importAesKeyFromSecret(secret);
  const cipherBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    enc.encode(plainText)
  );

  const ivB64 = toBase64(iv.buffer);
  const cipherB64 = toBase64(cipherBuffer);
  return `${ivB64}.${cipherB64}`;
}

export function stringifyKeyValue(obj: Record<string, unknown>): string {
  const entries = Object.entries(obj)
    .filter(([, v]) => v !== undefined && v !== null)
    .sort(([a], [b]) => {
      if (a < b) return -1;
      if (a > b) return 1;
      return 0;
    });
  return entries.map(([k, v]) => `${k}=${String(v)}`).join('&');
}

export async function encryptKeyValuePayload(obj: Record<string, unknown>): Promise<string> {
  const plain = stringifyKeyValue(obj);
  return encryptString(plain);
}

export function appendQueryParam(url: string, key: string, value: string): string {
  try {
    const u = new URL(url);
    u.searchParams.set(key, value);
    return u.toString();
  } catch (_e) {
    // Fallback for relative/invalid URLs
    const hasQuery = url.includes('?');
    const sep = hasQuery ? '&' : '?';
    const encoded = encodeURIComponent(value);
    return `${url}${sep}${encodeURIComponent(key)}=${encoded}`;
  }
}

export async function buildUrlWithEncryptedPayload(baseUrl: string, kv: Record<string, unknown>): Promise<string> {
  const encrypted = await encryptKeyValuePayload(kv);
  return appendQueryParam(baseUrl, 'payload', encrypted);
}

export async function decryptStringToPlain(encrypted: string): Promise<string> {
  // Handle base64 fallback format
  if (encrypted.startsWith('b64.')) {
    return atob(encrypted.slice(4));
  }

  const secret = ENCRYPTION_KEY || '';
  if (!secret) {
    throw new Error('Missing ENCRYPTION_KEY to decrypt AES-GCM payload');
  }

  const [ivB64, cipherB64] = encrypted.split('.', 2);
  if (!ivB64 || !cipherB64) {
    throw new Error('Invalid payload format. Expected "iv.cipher"');
  }

  const iv = fromBase64(ivB64);
  const cipher = fromBase64(cipherB64);
  const key = await importAesKeyFromSecretForDecrypt(secret);
  const plainBuffer = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    cipher
  );
  return new TextDecoder().decode(plainBuffer);
}

export function parseKeyValueString(plain: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!plain) return out;
  const pairs = plain.split('&');
  pairs.forEach((p) => {
    const eqIdx = p.indexOf('=');
    if (eqIdx === -1) return;
    const k = p.slice(0, eqIdx);
    const v = p.slice(eqIdx + 1);
    out[k] = v;
  });
  return out;
}

// Convenience helper to decode a URL param value from `payload` into its plain string and object map
export async function decodeEncryptedPayloadParam(encodedParamValue: string): Promise<{ plain: string; data: Record<string, string> }> {
  const decoded = decodeURIComponent(encodedParamValue);
  const plain = await decryptStringToPlain(decoded);
  return { plain, data: parseKeyValueString(plain) };
}


